// ======================================
// Working with data on the seller side (MAKE OFFERS). Data
// ======================================

import {makeAutoObservable} from "mobx";
//local
import axiosInstance from "../axios/index";
import {formFileData, generateOfferData} from "helpers/helper";
import RequestSection from "pages/RequestsToUs/components/SingleRequest/sections/RequestSection";
import OfferCard from "components/offerCard/OfferCard";
import ContractSection from "pages/RequestsToUs/components/SingleRequest/sections/ContractSection";
import MessagesSection from "pages/RequestsToUs/components/SingleRequest/sections/MessagesSection";
import {directionReplacement} from "helpers/helper";
import {defaultClosedAtFieldSearch} from "constants/user.consts";
import {emptyPublicRequest} from "constants/seller.consts";

class SellerStore {
  // STATE. Observable and computed part
  // ****** COMMON ******
  btnLoading = false; // loading status for buttons
  loading = true; // loading status for parts of the page

  // ****** REQUESTS ******
  unreadRequests = 0; // counter for badge with number of unreaded requests
  requests = []; // requests
  request = {}; // data of the current (viewed) request
  activeRequestTab = 0; // number of the active tab when viewing a specific request
  requestFilterParams = {
    sort: "UNDER_FIRST", // default sort value
  };

  get isNotPublicRequest() {
    return !!this.request?.RequestId;
  }
  // array of tabs when viewing a specific request
  get requestToUsTabs() {
    return [
      {
        title: "REQUEST",
        content: RequestSection,
        id: 0,
        hash: "#",
        visible: true,
      },
      {
        title: "OUR_OFFER",
        content: OfferCard,
        id: 1,
        hash: "#",
        visible: true,
        disabled: !this.isOffer,
        tabProps: {
          clientSide: "seller",
        },
      },
      {
        title: "CONTRACT",
        content: ContractSection,
        id: 2,
        hash: "#",
        visible: true,
        disabled: !this.isContract,
      },
      {
        title: "CHAT",
        content: MessagesSection,
        id: 3,
        hash: "#",
        visible: this.isNotPublicRequest,
        class: "messages-tab",
      },
    ];
  }

  // ****** OFFERS ******
  offers = []; // offers
  currentOfferId = ""; // id of the current (viewed) offer
  offerLoading = false; // loading status for offers page
  get offerFiles() {
    return this.request?.Offer?.OfferFiles;
  }
  get isOffer() {
    return !!this.request?.Offer?.id;
  }

  // ****** CONTRACTS ******
  contracts = []; // contracts
  currentContract = null; // data of the current (viewed) contract
  isContract = false; // status showing whether the request has a contract
  unsignedSellerContracts = 0; // counter for badge with number unsigned contracts on seller side
  // returns the status of the seller's signature in the current contract
  get contractOwnPersons() {
    return this.currentContract?.SellerSignatories;
  }
  // status for a fully signed own side contract
  get isFinishedOwnSignatures() {
    return (
      !!this.contractOwnPersons?.length &&
      this.contractOwnPersons?.every((user) => !!user.SellerSignId)
    );
  }
  // returns the status of the buyer's signature in the current contract
  get contractBuyerPersons() {
    return this.currentContract?.BuyerSignatories.filter((user) => !!user.BuyerSignId);
  }
  // status for a fully signed seller side contract
  get isFinishedOtherSignatures() {
    return (
      !!this.currentContract?.BuyerSignatories?.length &&
      this.currentContract?.BuyerSignatories?.every((user) => !!user.BuyerSignId)
    );
  }
  // status for a fully signed (finished) contract
  get isFinishedContract() {
    return this.currentContract?.isSigned;
  }

  // ****** PUBLIC RFPS ******
  constructor() {
    makeAutoObservable(this);
  }

  async getRequestsCountAnalytic(params = {}) {
    const emptyData = {own: 0, ours: 0, others: 0};
    try {
      const {data} = await axiosInstance.get("rfp/seller/search/non-readed", {params});
      return data;
    } catch (e) {
      return emptyData;
    }
  }

  // ACTIONS
  // ****** WORKING WITH REQUESTS (PROPOSALS FROM BUYER SIDE) ******

  // getting all requests
  async getRequests(page, logic = "all", additionalParams = {}) {
    this.loading = true;
    const params = {
      ...this.requestFilterParams,
      page,
      logic,
      ...additionalParams,
    };
    try {
      const {data} = await axiosInstance.get("rfp/seller/search", {params});
      this.loading = false;
      const requestRows = data.requests || [];
      const dataWithoutFinishedContracts =
        requestRows.filter((item) => !item.Offer?.Contract?.id) || [];
      this.requests = dataWithoutFinishedContracts;

      const response = {
        totalItems: data.totalItems,
        page: page || 1,
        totalPages: Math.ceil(data.totalItems / 20),
        requests: dataWithoutFinishedContracts,
      };
      return response;
    } catch (e) {
      this.loading = false;
      throw e;
    }
  }

  // getting one specific request
  async getRequest(id, notLoading) {
    this.loading = !notLoading;
    if (!id) return (this.request = {});
    return axiosInstance
      .get(`rfp/seller/${id}`, {suppressErrors: true})
      .then(({data}) => {
        this.setOfferId(data.Offer?.id);
        this.request = data;
      })
      .finally(() => {
        this.loading = false;
      });
  }
  // setting updated information on RFP after editing in store
  updateRequestInfo(request) {
    this.request = request;
  }

  // updating a specific request
  async updateRequest(value) {
    return axiosInstance
      .put(`rfp/seller/${this.request.id}`, value)
      .then(({data}) => this.getRequest(data.id, true));
  }

  // set bookmark for a specific request
  async setBookmarkRequest(id) {
    return axiosInstance
      .post(`rfp/seller/${id}/bookmark`)
      .then(() => this.getRequest(id, true));
  }

  // delete bookmark for a specific request
  async deleteBookmarkRequest(id) {
    return axiosInstance
      .delete(`rfp/seller/${id}/bookmark`)
      .then(() => this.getRequest(id, true));
  }

  // setting number for active tab of open request
  setActiveRequestTab(value) {
    this.activeRequestTab = value;
  }

  // setting filters for the requests
  setRequestFilterParams(values) {
    this.requestFilterParams = values;
    this.requestFilterParams = {...this.requestFilterParams, ...values};
  }

  // ****** WORKING WITH OFFERS ******

  // getting list of offers
  async getOffers(values) {
    this.loading = true;
    const params = {...directionReplacement(values)};
    try {
      const {data} = await axiosInstance.get("rfp/seller/offer/search", {params});
      this.loading = false;
      this.offers = data;
    } catch (e) {
      this.loading = false;
      throw e;
    }
  }

  // creating offer
  createOurOffer(values) {
    return axiosInstance
      .post("rfp/seller/offer/create", generateOfferData(values, this.request))
      .then(({data}) => {
        this.setOfferId(data.id);
        this.setOffer(data);
        return data;
      });
  }

  // getting the data of a specific offer
  getOffer(id) {
    this.offerLoading = true;
    return axiosInstance
      .get(`rfp/seller/offer/${id}`)
      .then(({data}) => {
        this.setOffer(data);
        return data;
      })
      .finally(() => (this.offerLoading = false));
  }

  // updating specific offer
  updateOurOffer(values) {
    const offerId = this.request.Offer.id;
    return axiosInstance
      .post(`rfp/seller/offer/${offerId}`, generateOfferData(values, this.request))
      .then(({data}) => this.setOffer(data));
  }

  // setting information about offer for current request in store
  setOffer(value) {
    this.request = {
      ...this.request,
      Offer: {...this.request.Offer, ...value},
    };
  }

  // updating list of offer documents in specific request in store after uploading file
  updateOfferDocuments(offerFiles) {
    const offer = !this.request.Offer
      ? {OfferFiles: offerFiles}
      : {
          ...this.request.Offer,
          OfferFiles: offerFiles,
        };
    this.request = {
      ...this.request,
      Offer: offer,
    };
  }

  // uploading specific file for offer
  async uploadSellerFile(file) {
    return axiosInstance.post(`rfp/seller/upload`, file).then(({data}) => data);
  }

  // setting id of current (viewed) offer
  setOfferId(id) {
    this.currentOfferId = id;
  }

  // ****** WORKING WITH CONTRACTS ******

  // getting list of contracts (finished)
  async getContracts(values) {
    this.loading = true;
    const params = {...directionReplacement(values)};
    try {
      const {data} = await axiosInstance.get("rfp/seller/contract/search", {
        ...directionReplacement(params),
      });
      this.loading = false;
      this.contracts = data;
    } catch (e) {
      this.loading = false;
      throw e;
    }
  }

  // getting one specific contract
  async getContract(id, timer) {
    this.loading = !timer;
    try {
      const {data} = await axiosInstance.get(`rfp/seller/contract/${id}`);
      this.currentContract = data;
      this.loading = false;
    } catch (e) {
      this.loading = false;
      throw e;
    }
  }

  // setting the status if there is a created contract in the request
  setContractStatus(status) {
    this.isContract = status;
  }

  // adding one signatory into the specific contract
  async addSignatoryOnContract(contractId, UserId) {
    try {
      await axiosInstance.post(`rfp/seller/contract/${contractId}/signatory`, {UserId});
      await this.getContract(contractId);
    } catch (e) {
      throw e;
    }
  }

  // remove one signatory into the specific contract
  async removeSignatoryOnContract(contractId, UserId) {
    try {
      await axiosInstance.delete(`rfp/seller/contract/${contractId}/signatory/${UserId}`);
      await this.getContract(contractId);
    } catch (e) {
      throw e;
    }
  }

  // getting contract pdf-file with watermarks
  async downloadPdfWatermarkContract(id, contractUrl) {
    try {
      const {data} = await axiosInstance.post(
        `/rfp/seller/contract/${id}/pdf-footer`,
        {contractUrl},
        {responseType: "blob"}
      );
      return data;
    } catch (e) {
      throw e;
    }
  }

  clearContract() {
    this.currentContract = null;
  }

  // ****** WORKING WITH NOTIFICATIONS ******

  // getting number of unreaded requests
  async getUnreadRequests() {
    try {
      const {data} = await axiosInstance.get("rfp/seller/get-unread-requests");
      this.unreadRequests = data;
    } catch (e) {
      throw e;
    }
  }

  // getting number unsigned contracts on seller side
  async getUnsignedSellerContracts(page) {
    try {
      const {data} = await axiosInstance.get("rfp/seller/get-unsigned-seller-contracts");

      this.unsignedSellerContracts = data;
    } catch (e) {
      throw e;
    }
  }

  // ****** WORKING WITH MESSAGES ******

  // getting all messages for specific RFP
  async getRfpMessages({rfpId}) {
    try {
      const {data} = await axiosInstance.get(`rfp/seller/${rfpId}/message/search`);
      return data;
    } catch (error) {
      throw error;
    }
  }

  // creating message for specific RFP
  async createRfpMessage({rfpId, message, files}) {
    try {
      const path = `rfp/seller/${rfpId}/message`;
      const values = {message, files};
      const {data} = await axiosInstance.post(path, values);
      return data;
    } catch (error) {
      throw error;
    }
  }

  // updating message for specific RFP
  async updateRfpMessage({id, rfpId, message, files}) {
    try {
      const path = `rfp/seller/${rfpId}/message/${id}`;
      const values = {message, files};
      const {data} = await axiosInstance.put(path, values);
      return data;
    } catch (error) {
      throw error;
    }
  }

  // deleting message for specific organization in specific RFP
  async deleteRfpMessage({id, rfpId, message}) {
    try {
      const path = `rfp/seller/${rfpId}/message/${id}`;
      const {data} = await axiosInstance.delete(path, {message});
      return data;
    } catch (error) {
      throw error;
    }
  }

  // uploading file for message
  messageUploadFile({rfpId, file}) {
    return axiosInstance
      .post(`rfp/seller/${rfpId}/message/upload`, formFileData(file))
      .then(({data}) => {
        return data;
      });
  }

  // ****** WORKING WITH PUBLIC RFPS ******

  // getting list of public rfps
  async getPublicRequests(filters) {
    this.loading = true;
    const {tab, ...params} = {...filters};
    try {
      const {data} = await axiosInstance.get("rfp-public/search", {params});
      return data;
    } catch (e) {
      throw e;
    } finally {
      this.loading = false;
    }
  }

  // getting one specific request
  getPublicRequest(id, notLoading) {
    this.loading = !notLoading;
    return axiosInstance
      .get(`rfp-public/${id}`)
      .then(({data}) => {
        this.setOfferId(null);
        this.request = {...emptyPublicRequest, Request: data};
        return data;
      })
      .finally(() => (this.loading = false));
  }

  // setting interest status to public rfp (creating proposal)
  setInterestPublicRfp(requestId, values) {
    return axiosInstance
      .post(`rfp-public/${requestId}/proposal`, values)
      .then(({data}) => {
        this.setOfferId(data.Offer?.id);
        this.request = data;
      });
  }

  // setting not interest status to public rfp (deleting proposal)
  setNotInterestPublicRfp(requestId) {
    return axiosInstance
      .delete(`rfp-public/${requestId}/proposal`)
      .then(({data}) => this.getPublicRequest(requestId, true));
  }

  // set bookmark for a specific request
  async setBookmarkPublicRfp(id) {
    return axiosInstance
      .post(`rfp-public/${id}/bookmark`)
      .then(() => this.getPublicRequest(id, true));
  }

  // delete bookmark for a specific request
  async deleteBookmarkPublicRfp(id) {
    return axiosInstance
      .delete(`rfp-public/${id}/bookmark`)
      .then(() => this.getPublicRequest(id, true));
  }

  // ****** WORKING WITH EXTERNAL RFPS ******

  // getting list of public rfps
  async getExternalRequests(filters) {
    this.loading = true;
    const {tab, ...params} = {...filters};
    try {
      const {data} = await axiosInstance.get("rfp-external/search", {params});
      return {
        requests: data.data,
        totalItems: data.totalItems,
        currentPage: data.currentPage,
        totalPages: data.totalPages,
        totalUnfilteredItems: data.totalUnfilteredItems,
      };
    } catch (e) {
      throw e;
    } finally {
      this.loading = false;
    }
  }

  // getting one specific request
  getExternalRequest(id, notLoading) {
    this.loading = !notLoading;
    return axiosInstance
      .get(`rfp-external/search?search=${id}`)
      .then(({data}) => data?.data?.[0])
      .finally(() => (this.loading = false));
  }

  // getExternalRequest(id, notLoading) {
  // TODO: should trigger AI announcement
  // this.loading = !notLoading;
  // return axiosInstance
  //   .get(`rfp-external/${id}`)
  //   .then(({data}) => {
  //     this.setOfferId(null);
  //     this.request = {...emptyPublicRequest, Request: data};
  //     return data;
  //   })
  //   .finally(() => (this.loading = false));
  // }

  // AI-generated announcement (rich text) for an external RFT
  getExternalRftAnnouncement(id) {
    return axiosInstance
      .get(`rfp-external/${id}/announcement`, {suppressErrors: true})
      .then(({data}) => data);
  }

  // ****** WORKING WITH AVAILABLE REGIONS ******

  // fetch available regions from server
  async getAvailableRegions(logic = "own") {
    try {
      const {data} = await axiosInstance.get(
        `rfp/seller/search/available-regions?logic=${logic}`
      );
      return data;
    } catch (e) {
      return [];
    }
  }
}

const seller = new SellerStore();
export default seller;
